import React, { useState, useRef } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { PatternConfig, OvernightOption } from "../../utils/calendarUtils";

let idCounter = 0;
function useStableId(prefix: string): string {
  const ref = useRef<string>("");
  if (!ref.current) {
    idCounter += 1;
    ref.current = `${prefix}-${idCounter}`;
  }
  return ref.current;
}

export interface PatternControlsProps {
  onPopulate: (pattern: PatternConfig) => void;
}

const FREQUENCY_OPTIONS: { value: PatternConfig["frequency"]; labelKey: string }[] = [
  { value: "every", labelKey: "calendar.frequency.every" },
  { value: "everyOther", labelKey: "calendar.frequency.everyOther" },
];

const OVERNIGHT_OPTIONS: { value: OvernightOption; labelKey: string }[] = [
  { value: "sunday", labelKey: "calendar.overnights.sunday" },
  { value: "monday", labelKey: "calendar.overnights.monday" },
  { value: "tuesday", labelKey: "calendar.overnights.tuesday" },
  { value: "wednesday", labelKey: "calendar.overnights.wednesday" },
  { value: "thursday", labelKey: "calendar.overnights.thursday" },
  { value: "friday", labelKey: "calendar.overnights.friday" },
  { value: "saturday", labelKey: "calendar.overnights.saturday" },
  { value: "weekend", labelKey: "calendar.overnights.weekend" },
  { value: "weekdays", labelKey: "calendar.overnights.weekdays" },
];

const PatternControls: React.FC<PatternControlsProps> = ({ onPopulate }) => {
  const { t } = useTranslation();
  const [frequency, setFrequency] = useState<PatternConfig["frequency"] | "">("");
  const [overnights, setOvernights] = useState<OvernightOption | "">("");
  const [startDate, setStartDate] = useState<string>("");
  const [showErrors, setShowErrors] = useState(false);

  // Unique IDs for aria-describedby associations
  const instructionsId = useStableId("pattern-instructions");
  const frequencyErrorId = useStableId("freq-error");
  const overnightsErrorId = useStableId("overnight-error");
  const startDateErrorId = useStableId("start-date-error");

  const isPopulateDisabled = !frequency || !overnights || !startDate;

  const handlePopulate = () => {
    if (isPopulateDisabled) {
      setShowErrors(true);
      return;
    }
    if (frequency && overnights && startDate) {
      setShowErrors(false);
      onPopulate({ frequency, overnights, startDate });
    }
  };

  const frequencyError = showErrors && !frequency;
  const overnightsError = showErrors && !overnights;
  const startDateError = showErrors && !startDate;

  return (
    <Box
      role="group"
      aria-labelledby={instructionsId}
      sx={{ mb: 2 }}
    >
      {/* Instructions for pattern controls */}
      <Typography
        id={instructionsId}
        variant="body2"
        sx={{ mb: 1, color: "text.secondary" }}
      >
        {t("calendar.patternInstructions", {
          defaultValue:
            "Select a frequency, overnight schedule, and start date to auto-populate the calendar for the active parent.",
        })}
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }}>
        <FormControl
          size="small"
          sx={{ minWidth: 150 }}
          error={frequencyError}
          required
        >
          <InputLabel id="frequency-label">{t("calendar.frequencyLabel")}</InputLabel>
          <Select
            labelId="frequency-label"
            value={frequency}
            label={t("calendar.frequencyLabel")}
            onChange={(e) => {
              setFrequency(e.target.value as PatternConfig["frequency"]);
              if (showErrors) setShowErrors(false);
            }}
            inputProps={{
              "aria-required": true,
              "aria-invalid": frequencyError || undefined,
              "aria-describedby": frequencyError ? frequencyErrorId : undefined,
            }}
            sx={{ minHeight: 44 }}
          >
            {FREQUENCY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </MenuItem>
            ))}
          </Select>
          {frequencyError && (
            <FormHelperText id={frequencyErrorId} error>
              {t("calendar.frequencyRequired", { defaultValue: "Frequency is required" })}
            </FormHelperText>
          )}
        </FormControl>

        <FormControl
          size="small"
          sx={{ minWidth: 200 }}
          error={overnightsError}
          required
        >
          <InputLabel id="overnights-label">{t("calendar.overnightsLabel")}</InputLabel>
          <Select
            labelId="overnights-label"
            value={overnights}
            label={t("calendar.overnightsLabel")}
            onChange={(e) => {
              setOvernights(e.target.value as OvernightOption);
              if (showErrors) setShowErrors(false);
            }}
            inputProps={{
              "aria-required": true,
              "aria-invalid": overnightsError || undefined,
              "aria-describedby": overnightsError ? overnightsErrorId : undefined,
            }}
            sx={{ minHeight: 44 }}
          >
            {OVERNIGHT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </MenuItem>
            ))}
          </Select>
          {overnightsError && (
            <FormHelperText id={overnightsErrorId} error>
              {t("calendar.overnightsRequired", { defaultValue: "Overnight schedule is required" })}
            </FormHelperText>
          )}
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }} error={startDateError} required>
          <TextField
            size="small"
            type="date"
            label={t("calendar.patternStartDateLabel")}
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              if (showErrors) setShowErrors(false);
            }}
            InputLabelProps={{ shrink: true }}
            InputProps={{ sx: { minHeight: 44 } }}
            error={startDateError}
            helperText={
              startDateError
                ? t("calendar.startDateRequired", { defaultValue: "Start date is required" })
                : undefined
            }
            FormHelperTextProps={{
              id: startDateErrorId,
            }}
            inputProps={{
              "aria-required": true,
              "aria-invalid": startDateError || undefined,
              "aria-describedby": startDateError ? startDateErrorId : undefined,
            }}
          />
        </FormControl>

        {/* WCAG 2.5.5: Touch target minimum 44x44px */}
        <Button
          variant="contained"
          onClick={handlePopulate}
          aria-label={t("calendar.populateButton", { defaultValue: "Apply pattern to calendar" })}
          sx={{ minHeight: 44, minWidth: 44, px: 3 }}
        >
          {t("calendar.populateButton")}
        </Button>
      </Box>
    </Box>
  );
};

export default PatternControls;
